import "./MyOrders.scss";
import Navbar from "../../components/CommonComponents/Navbar/Navbar";
import SnackBar, {
  SnackTypes,
} from "../../components/UtilityComponents/SnackBar/SnackBar";
import logAnalyticsEvent from '../../services/AnalyticsManager/AnalyticsManager';
import { GGAnalyticsEvents } from '../../services/AnalyticsManager/GGAnalyticsEvents';

import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import InsertCommentOutlinedIcon from "@mui/icons-material/InsertCommentOutlined";
import EventRepeatOutlinedIcon from "@mui/icons-material/EventRepeatOutlined";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
// import MoreVertOutlinedIcon from '@mui/icons-material/MoreVertOutlined';
import DescriptionIcon from "@mui/icons-material/Description";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import GGButton from "../../components/CommonComponents/GGButton/GGButton";
import { useNavigate } from "react-router-dom";
import React, { useEffect, useRef, useState } from "react";
import { OrderAPIHandler } from "../../services/NetworkClient/BackendAPIs/OrderAPIHandler";
import { Order, OrderStatus } from "../../Models/Order";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { SessionManager } from "../../services/SessionManager/SessionManager";
import { ProgressReviewAPIHandler } from "../../services/NetworkClient/BackendAPIs/ProgressReviewAPIHandler";
import GGLoaderButton from "../../components/CommonComponents/GGLoaderButton/GGLoaderButton";
import OrderReviewFormCarouselComponent from "../../components/PageComponents/OrderComponents/OrderReviewFormCarouselComponent/OrderReviewFormCarouselComponent";
import StarRating from "../../components/CommonComponents/StarRating/StarRating";
import { ReviewAPIHandler } from "../../services/NetworkClient/BackendAPIs/ReviewAPIHandler";
import { Review } from "../../Models/Review";
import { formatCurrencyIndian } from "../../utils/MoneyConversionUtils";
import { ErrorLogger } from "../../utils/Logger";
import Footer from "../../components/CommonComponents/Footer/Footer";

const MyOrders = () => {
  const navigate = useNavigate();
  const [ordersList, setOrdersList] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [
    isAskForProgressReviewBtnDisabled,
    setIsAskForProgressReviewBtnDisabled,
  ] = useState(false);

  // Snackbar based status handling
  const snackBarRef = useRef<{ showSnackbar(): any }>(null);
  const [snackBarParams, setSnackBarParams] = useState({
    message: "",
    state: SnackTypes.error,
  });

  const showSnackbar = (message: string, state: SnackTypes) => {
    setSnackBarParams({ message: message, state: state });
    snackBarRef.current?.showSnackbar();
  };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const orders = await OrderAPIHandler.shared.getOrdersByBuyerId();
      setOrdersList(orders);
    } catch (error) {
      ErrorLogger.log(error, 'MyOrders.fetchOrders');
    } finally {
      setLoading(false);
    }
  };

  const askForProgressReview = async (orderId: string) => {
    try {
      const message =
        await ProgressReviewAPIHandler.shared.askForProgressReview(orderId);
      showSnackbar(
        message ?? "Freelancer has been asked for a progress review.",
        SnackTypes.success
      );
      setIsAskForProgressReviewBtnDisabled(true);
    } catch (error) {
      ErrorLogger.log(error, 'MyOrders.askForProgressReview');
      showSnackbar(error as string, SnackTypes.error);
    }
  };

  useEffect(() => {
    document.title = "GenieGig | My Orders";
    logAnalyticsEvent(GGAnalyticsEvents.BUYER_ORDERS_PAGE_LOADED);
    fetchOrders();
  }, []);

  const handleViewGigBtnClick = (gigId: string) => {
    navigate("/gig/" + gigId);
  };

  const BuyerOrderCard = ({ orderData }: { orderData: Order }) => {
    const [invoiceDownloadLoader, setInvoiceDownloadLoader] = useState(false);
    const [receiptDownloadLoader, setReceiptDownloadLoader] = useState(false);

    // Calculate grand total for client perspective
    const getGrandTotalInINR = () => {
      if (!orderData.orderSummary) return "Unable to load order summary";
      let total = orderData.orderSummary.orderTotals.totalAmountPayableByBuyer ?? 0
      return formatCurrencyIndian(total);
    };

    const downloadInvoice = async (orderId: string) => {
      try {
        setInvoiceDownloadLoader(true);

        // const blob = await OrderAPIHandler.shared.downloadOrderInvoicePdf(orderId);
        const blob = await OrderAPIHandler.shared.generateTaxInvoiceForBuyerAPI(
          orderId
        );

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `Order_${orderId}_Invoice.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        ErrorLogger.log(err, 'MyOrders.BuyerOrderCard.downloadInvoice');
      } finally {
        setInvoiceDownloadLoader(false);
      }
    };

    const downloadOrderReceipt = async (orderId: string) => {
      try {
        setReceiptDownloadLoader(true);

        const blob = await OrderAPIHandler.shared.downloadOrderReceiptPdfForBuyer(
          orderId
        );

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `Order_${orderId}_Receipt.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        ErrorLogger.log(err, 'MyOrders.BuyerOrderCard.downloadOrderReceipt');
      } finally {
        setReceiptDownloadLoader(false);
      }
    };

    const CardLabel = ({ orderData }: { orderData: Order }) => {
      switch (orderData.status) {
        case OrderStatus.init:
          return <Typography className="label init">Initialized</Typography>;
        case OrderStatus.pending:
          return (
            <Typography className="label inprogress">On the way</Typography>
          );
        case OrderStatus.readyForDelivery:
          return (
            <Typography className="label awaiting">
              Awaiting Your Approval
            </Typography>
          );
        case OrderStatus.approvedByBuyer:
        case OrderStatus.completed:
          return (
            <Typography className="label order-completed">
              Order Completed
            </Typography>
          );
        case OrderStatus.overdue:
          return <Typography className="label overdue">Overdue</Typography>;
        case OrderStatus.cancelled:
          return <Typography className="label cancelled">Cancelled</Typography>;
        default:
          return <></>;
      }
    };

    const ActionBtn = ({ orderData }: { orderData: Order }) => {
      switch (orderData.status) {
        case OrderStatus.pending:
          return (
            <GGButton
              className="headBtn2 review"
              variant="outlined"
              disableRipple
              disabled={isAskForProgressReviewBtnDisabled}
              startIcon={<EventRepeatOutlinedIcon />}
              onClick={() => askForProgressReview(orderData.id)}
            >
              <Typography fontSize={10}>Ask for progress review</Typography>
            </GGButton>
          );

        case OrderStatus.approvedByBuyer:
        case OrderStatus.completed:
          return (
            <div style={{ display: "flex" }}>
              <GGLoaderButton
                className="headBtn1 invoice"
                variant="outlined"
                isLoading={invoiceDownloadLoader}
                circularProgressColorHex={"#bcbcbc"}
                disableRipple
                startIcon={<PictureAsPdfIcon className="reviewIcon" />}
                onClick={() => downloadInvoice(orderData.id)}
              >
                <Typography fontSize={11}>Download Invoice</Typography>
              </GGLoaderButton>
              <GGLoaderButton
                className="headBtn1 receipt"
                variant="outlined"
                isLoading={receiptDownloadLoader}
                circularProgressColorHex={"#bcbcbc"}
                disableRipple
                startIcon={<PictureAsPdfIcon className="reviewIcon" />}
                onClick={() => downloadOrderReceipt(orderData.id)}
              >
                <Typography fontSize={11}>Download Receipt</Typography>
              </GGLoaderButton>
              <GGButton
                className="headBtn2 go-to-order-summary-btn"
                variant="outlined"
                disableRipple
                startIcon={<DescriptionIcon className="order-summary-icon" />}
                onClick={() => navigate("/order-details/" + orderData.id)}
              >
                <Typography fontSize={11}>View Order</Typography>
              </GGButton>
            </div>
          );

        case OrderStatus.cancelled:
        case OrderStatus.overdue:
          return <></>;

        default:
          return <></>;
      }
    };

    const RatingsForm = () => {
      const [reviewComments, setReviewComments] = useState("");
      const [rating, setRating] = useState(0);

      const handleRatingReviewSubmit = async (order: Order) => {
        if (rating === 0) {
          showSnackbar("Please provide a rating!", SnackTypes.error);
          return;
        }
        if (reviewComments.trim() === "") {
          showSnackbar("Please provide a review!", SnackTypes.error);
          return;
        }

        const reviewData = {
          gigId: order.metaData.gig.id,
          order: order.id,
          rating: rating,
          attachments: [],
          comment: reviewComments,
        };

        setLoading(true);
        ReviewAPIHandler.shared
          .createReview(reviewData)
          .then(() => {
            showSnackbar("Review submitted successfully!", SnackTypes.success);
            setLoading(false);
            onSubmitCompletion();
          })
          .catch((error) => {
            setLoading(false);
            showSnackbar(
              (error as string) || "Failed to submit review!",
              SnackTypes.error
            );
          });
      };

      const onSubmitCompletion = () => {
        window.location.reload();
      };

      return (
        <>
          <StarRating
            className="star-rating-row"
            value={rating}
            onChange={setRating}
          />

          <TextField
            style={{ width: "100%" }}
            value={reviewComments}
            onChange={(e) => setReviewComments(e.target.value)}
            className="feedback-textarea"
            id="outlined-multiline-static"
            multiline
            fullWidth
            rows={2.5}
            placeholder="How did you like this service...?"
          />

          <GGLoaderButton
            className="submit-review-btn"
            variant="outlined"
            style={{ width: "fit-content", border: "none" }}
            isLoading={loading}
            onClick={() => handleRatingReviewSubmit(orderData)}
          >
            Submit Review
          </GGLoaderButton>
        </>
      );
    };

    const StaticRatings = ({ review }: { review: Review }) => {
      return (
        <Box
          className="rating-and-review-content"
          style={{
            width: "100%",
            marginTop: "8px",
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Typography fontSize={"16px"} className="rating-and-review-text">
            You hava rated <b>{orderData?.metaData.seller.fullName}</b>!
          </Typography>

          <StarRating
            className="star-rating-row"
            disabled={true}
            value={review.rating}
          />

          <Box
            flexDirection={"column"}
            display={"flex"}
            padding={"20px"}
            width={"100%"}
            sx={{ border: "1px solid rgb(215, 215, 215)" }}
          >
            <Typography
              fontSize={"12px"}
              fontWeight={1000}
              marginBottom={"10px"}
            >
              Comments -
            </Typography>

            <Typography fontSize={"16px"}>{review.comment}</Typography>
          </Box>
        </Box>
      );
    };

    return (
      <div className="buyerOrderCardHolder">
        <div className="buyerOrderCard">
          <div className="head">
            <div className="headLeft">
              <CardLabel orderData={orderData} />
              <Typography className="data" fontSize={12}>
                Order ID: #{orderData.id}
              </Typography>
              <span>|</span>
              <Typography className="data" fontSize={12}>
                Placed on:{" "}
                {new Date(
                  orderData.createdAt ?? Date.now()
                ).toLocaleDateString()}
              </Typography>
            </div>
            <div className="headRight">
              <GGButton
                className="headBtn1"
                variant="outlined"
                disableRipple
                startIcon={<InsertCommentOutlinedIcon className="chatIcon" />}
                onClick={() =>
                  navigate("/my-messages", {
                    state: { channelId: orderData.metaData.channelId },
                  })
                }
              >
                <Typography fontSize={12}>Go to chat</Typography>
              </GGButton>
              <ActionBtn orderData={orderData} />
              {/* <MoreVertOutlinedIcon /> */}
            </div>
          </div>
          <div className="orderBody">
            <div className="orderBodyLeft">
              <Avatar
                className="icon"
                alt=""
                src={orderData.metaData.gig.coverImg}
              />
              <div className="body">
                <Typography
                  className="gig-title"
                  fontSize={16}
                  marginBottom={5}
                >
                  {orderData.metaData.gig.title}
                </Typography>
                <div className="gigSeller">
                  Sold By{" "}
                  <Avatar
                    className="sellerIcon"
                    alt=""
                    src={orderData.metaData.seller.profileImage}
                  />{" "}
                  <a
                    href={"/profile/" + orderData.metaData.seller.buyer}
                    className="sellerName"
                  >
                    {orderData.metaData.seller.fullName}
                  </a>
                </div>
                <div className="gigbottom">
                  <GGButton className="btn1" variant="text" disableClickShrink onClick={() => handleViewGigBtnClick(orderData.metaData.gig.id)}>View Gig</GGButton>
                  <div className="btn2">Total Amount: <span>{getGrandTotalInINR()}</span></div>
                </div>
              </div>
            </div>
            <div className="separator-line"></div>
            <div className="orderBodyRight">
              <Typography className="deliveryDateTitle">Delivery On</Typography>
              <Typography className="deliveryDate">
                {new Date(
                  orderData.deliveryTimeStamp ?? Date.now()
                ).toLocaleDateString()}
              </Typography>
              {orderData.status === OrderStatus.readyForDelivery && (
                <>
                  <GGButton
                    className="headBtn2 review-delivery-btn"
                    variant="outlined"
                    disableRipple
                    startIcon={<EventRepeatOutlinedIcon />}
                    onClick={() => navigate("/order-details/" + orderData.id)}
                  >
                    Review Delivery
                  </GGButton>
                  <i>
                    <Typography
                      className="warningText"
                      fontSize={12}
                      fontWeight={500}
                      fontStyle="italic"
                      textAlign="center"
                      color="red"
                      marginTop={1}
                      marginBottom={1}
                    >
                      If you do not review the delivery, the order will be
                      marked as completed automatically after 48 hours.
                    </Typography>
                  </i>
                </>
              )}

              {(orderData.status === OrderStatus.approvedByBuyer ||
                orderData.status === OrderStatus.completed) && (
                <div
                  style={{
                    width: "100%",
                    marginTop: "12px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  {orderData.review ? (
                    <StaticRatings review={orderData.review} />
                  ) : (
                    <RatingsForm />
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="orderSummary">
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreOutlinedIcon />}
                aria-controls="panel2-content"
                id="panel2-header"
              >
                <Typography
                  className="orderHead"
                  style={{ fontSize: "14px", fontWeight: "bold" }}
                >
                  Order Summary
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <table className="orderSummaryTable">
                  <tbody>
                    {/* Main Order Breakdown Section */}
                    <tr>
                      <td className="order-summary-section-title" colSpan={2}>
                        <b>Main Order Breakdown</b>
                      </td>
                    </tr>
                    
                    {/* Primary Transaction */}
                    <tr>
                      <td className="order-summary-item-label">Gig Amount</td>
                      <td className="order-summary-item-value">{formatCurrencyIndian(orderData.orderSummary?.primaryTransaction.basePrice ?? 0)}</td>
                    </tr>
                    
                    {/* Platform Fee */}
                    <tr>
                      <td className="order-summary-item-label">Platform Fee ({(orderData.orderSummary?.primaryTransaction.buyerFinancials.platformFee.percentage ?? 0) * 100}%)</td>
                      <td className="order-summary-item-value">{formatCurrencyIndian(orderData.orderSummary?.primaryTransaction.buyerFinancials.platformFee.actualFeeCharged ?? 0)}</td>
                    </tr>
                    
                    {/* GST */}
                    <tr>
                      <td className="order-summary-item-label">GST @ {(orderData.orderSummary?.primaryTransaction.buyerFinancials.gstDetails.ratePercentage ?? 0) * 100}%</td>
                      <td className="order-summary-item-value">{formatCurrencyIndian(orderData.orderSummary?.primaryTransaction.buyerFinancials.gstDetails.actualGstAmount ?? 0)}</td>
                    </tr>
                    
                    {/* Promo Discount if applicable */}
                    {orderData.orderSummary?.primaryTransaction.promoDetails?.promoCode && (
                      <tr>
                        <td className="order-summary-item-label">Promo Applied ({orderData.orderSummary.primaryTransaction.promoDetails.promoCode})</td>
                        <td className="order-summary-item-value promo-code">- {formatCurrencyIndian(orderData.orderSummary.primaryTransaction.promoDetails.actualDiscountValue)}</td>
                      </tr>
                    )}
                    
                    {/* Main Order Subtotal */}
                    <tr className="order-summary-subtotal-row">
                      <td className="order-summary-subtotal-title"><b>Main Order Subtotal</b></td>
                      <td className="order-summary-subtotal-value" style={{ textAlign: "right" }}><b>{formatCurrencyIndian(orderData.orderSummary?.primaryTransaction.buyerFinancials.totalAmountPayable ?? 0)}</b></td>
                    </tr>
                    
                    {/* Revision Transactions */}
                    {orderData.orderSummary?.revisionTransactions && orderData.orderSummary.revisionTransactions.length > 0 && (
                      <>
                        <tr>
                          <td colSpan={2}>
                            <hr className="order-summary-section-divider" />
                          </td>
                        </tr>
                        <tr>
                          <td className="order-summary-section-title" colSpan={2}>
                            <b>Revision Transactions</b>
                          </td>
                        </tr>
                        
                        {/* All Revisions with proper numbering */}
                        {orderData.orderSummary.revisionTransactions.map((revision, index) => {
                            const isFreeRevision = revision.buyerFinancials.totalAmountPayable === 0;
                            const revisionNumber = index + 1;
                            
                            return (
                                <React.Fragment key={revision.transactionId}>
                                    <tr className="revision-spacer">
                                        <td colSpan={2}></td>
                                    </tr>
                                    
                                    {isFreeRevision ? (
                                        <tr>
                                            <td className="order-summary-item-label">Included Revision #{revisionNumber}</td>
                                            <td className="order-summary-item-value free">FREE</td>
                                        </tr>
                                    ) : (
                                        <>
                                            <tr>
                                                <td className="order-summary-item-label">Paid Revision #{revisionNumber} - Base Amount</td>
                                                <td className="order-summary-item-value">{formatCurrencyIndian(revision.basePrice)}</td>
                                            </tr>
                                            <tr>
                                                <td className="order-summary-item-label">Platform Fee ({(revision.buyerFinancials.platformFee.percentage) * 100}%)</td>
                                                <td className="order-summary-item-value">{formatCurrencyIndian(revision.buyerFinancials.platformFee.actualFeeCharged)}</td>
                                            </tr>
                                            <tr>
                                                <td className="order-summary-item-label">GST @ {(revision.buyerFinancials.gstDetails.ratePercentage) * 100}%</td>
                                                <td className="order-summary-item-value">{formatCurrencyIndian(revision.buyerFinancials.gstDetails.actualGstAmount)}</td>
                                            </tr>
                                            {revision.promoDetails?.promoCode && (
                                                <tr>
                                                    <td className="order-summary-item-label">Promo Applied ({revision.promoDetails.promoCode})</td>
                                                    <td className="order-summary-item-value promo-code">- {formatCurrencyIndian(revision.promoDetails.actualDiscountValue)}</td>
                                                </tr>
                                            )}
                                            <tr className="order-summary-revision-subtotal-row">
                                                <td className="order-summary-revision-subtotal-title"><b>Revision #{revisionNumber} Subtotal</b></td>
                                                <td className="order-summary-revision-subtotal-value" style={{ textAlign: "right" }}><b>{formatCurrencyIndian(revision.buyerFinancials.totalAmountPayable)}</b></td>
                                            </tr>
                                        </>
                                    )}
                                </React.Fragment>
                            );
                        })}
                      </>
                    )}
                    
                    <tr>
                      <td colSpan={2}>
                        <hr className="order-summary-divider" />
                      </td>
                    </tr>
                    <tr className="order-summary-grand-total-row">
                      <td className="order-summary-grand-total-title"><b>Total Amount Paid</b></td>
                      <td className="order-summary-grand-total-value" style={{ textAlign: "right" }}><b>{getGrandTotalInINR()}</b></td>
                    </tr>
                  </tbody>
                </table>
              </AccordionDetails>
            </Accordion>
          </div>
        </div>
      </div>
    );
  };

  const BuyerOrderPageHeader = () => (
    <>
      <div className="buyerOrderExtraSpace"></div>
      <div className="buyerOrderTab">
        <div className="buyerOrderTabLeft">
          <ShoppingCartOutlinedIcon /> <Typography>My Orders</Typography>
        </div>
        {/* {// @TODO: Add filter options} */}
        {/* <div className="buyerOrderTabRight">
            <div className="filterOption active">Ongoing</div>
            <div className="filterOption">Past</div>
            <div className="filterOption">All</div>
            <div className="filterSetting"><TuneOutlinedIcon /></div>
          </div> */}
      </div>
    </>
  );

  const getUnratedOrders = () => {
    return ordersList.filter(
      (order) =>
        !order.review &&
        (order.status === OrderStatus.approvedByBuyer ||
          order.status === OrderStatus.completed)
    );
  };

  return (
    <div className="page-root">
      <Navbar useScrollVersion={true} />
      <SnackBar
        ref={snackBarRef}
        severity={snackBarParams.state}
        alertDuration={5000}
        message={snackBarParams.message}
      />
      <BuyerOrderPageHeader />

      {SessionManager.shared.isSeller() && (
        <Typography
          variant="subtitle2"
          sx={{ mb: 2, color: "#6c6c6c", width: "100%", fontStyle: "italic" }}
        >
          These are the orders you bought as a client. To see the list of orders
          you fulfilled as freelancer, go to your Dashboard and click on the{" "}
          <b>Orders</b> tab.
        </Typography>
      )}

      {getUnratedOrders().length > 0 && (
        <OrderReviewFormCarouselComponent
          reviewPendingOrders={getUnratedOrders()}
        />
      )}
      {loading ? (
        <CircularProgress
          className="circular-progress"
          thickness={5}
          size={50}
          sx={{ width: "100%", marginTop: "75px", color: "grey" }}
        />
      ) : (
        <>
          {ordersList.length !== 0 ? (
            <>
              {ordersList.map((order) => (
                <BuyerOrderCard orderData={order} />
              ))}
            </>
          ) : (
            <>
              <Typography variant="h5" fontWeight={1000} marginTop={"75px"}>
                You do not have any orders yet.
              </Typography>
            </>
          )}
        </>
      )}
      <div
        className="buyerOrderExtraSpace"
        style={{ height: "50vh", width: "1px" }}
      ></div>
      <Footer />
    </div>
  );
};

export default MyOrders;
